import { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { WorkflowPanel } from "./WorkflowPanel";
import { WORKFLOWS } from "./workflows-data";

const AUTOPLAY_MS = 5000;

const WorkflowTab = ({ wf, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`workflow-tab-${wf.num}`}
    aria-label={`View ${wf.name} workflow`}
    aria-current={active}
    className={`flex items-center gap-3 rounded-xl border bg-white px-4 py-3.5 text-left w-full min-w-[230px] lg:min-w-0 transition-[border-color,box-shadow] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563FF] focus-visible:ring-offset-2 ${
      active
        ? "border-[#2563FF] shadow-soft"
        : "border-slate-200 hover:border-nova-bluelight"
    }`}
  >
    <span className="w-8 h-8 rounded-lg bg-nova-mist flex items-center justify-center shrink-0">
      <wf.icon size={16} strokeWidth={1.7} className="text-nova-blue" />
    </span>
    <span className={`text-[11px] font-bold ${active ? "text-nova-blue" : "text-nova-muted"}`}>
      {wf.num}
    </span>
    <span className="flex-1 text-sm font-semibold text-nova-navy whitespace-nowrap">
      {wf.name}
    </span>
    {active ? (
      <span className="w-7 h-7 rounded-full bg-nova-blue flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgb(37,99,235,0.4)]">
        <ChevronRight size={14} strokeWidth={2.5} className="text-white" />
      </span>
    ) : (
      <ChevronRight size={15} className="text-slate-300 shrink-0" />
    )}
  </button>
);

export const NovaAssistant = () => {
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const [hidden, setHidden] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (hover || hidden) {
      clearTimer();
      return;
    }
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % WORKFLOWS.length);
    }, AUTOPLAY_MS);
    return clearTimer;
  }, [hover, hidden, index, clearTimer]);

  const wf = WORKFLOWS[index];

  return (
    <section
      id="workflows"
      data-testid="nova-assistant-section"
      className="scroll-mt-36 bg-white"
    >
      <div className="mx-auto max-w-7xl 2xl:max-w-[1360px] px-5 sm:px-8 2xl:pl-[72px] py-24 lg:py-32">
        <div className="grid lg:grid-cols-[280px_1fr] gap-12 lg:gap-10">
          <Reveal className="min-w-0">
            <div>
              <span
                data-testid="chapter-04-number"
                className="text-5xl lg:text-6xl font-extrabold tracking-tighter text-nova-blue leading-none"
              >
                04
              </span>
              <span className="block mt-3 text-[11px] font-bold tracking-[0.28em] uppercase text-nova-muted leading-relaxed">
                Nova Gets It
                <br />
                Done
              </span>
              <h2 className="mt-10 text-3xl sm:text-4xl font-bold tracking-tighter text-nova-navy leading-[1.08]">
                A Real Workflow.
                <br />
                <span className="text-nova-blue">Handled End to End.</span>
              </h2>
              <p className="mt-4 text-sm text-nova-slate leading-relaxed">
                Nova takes care of the busywork across your systems—so your team
                can focus on what matters.
              </p>

              <div
                data-testid="workflow-tabs"
                role="tablist"
                aria-label="Workflows"
                className="mt-9 flex lg:flex-col gap-3 overflow-x-auto pb-2 lg:pb-0 -mx-1 px-1"
              >
                {WORKFLOWS.map((w, i) => (
                  <WorkflowTab
                    key={w.id}
                    wf={w}
                    active={i === index}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="min-w-0">
            <div
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              className="md:min-h-[1020px] lg:min-h-[895px] xl:min-h-[790px] 2xl:min-h-[845px]"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={wf.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                  <WorkflowPanel wf={wf} />
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
